/**
 * Created by falknisius on 30.05.17.
 *
 *
 *
 * subscribeChannel:
 *      channelName, StoreAction
 *      handle connection in background
 *      if receive call storeAction
 *
 * Queue Consumer
 *      connect:
 *          encapsulate Ably connect
 *          get instanceId
 *          call auth server with applicationInstanceId for Ably token
 *
 *      register for event come online
 */

import { BaseRealtime, WebSocketTransport, FetchRequest, RealtimePresence } from 'ably/modular';

const RECONNECTION_TIMEOUT = 5000 // ms
const ABLY_API_KEY = 'CuzkTg.KigbpQ:Xgie6yBgMXoH7wKb'

class MessageService {

  /**
   * initialize ably connection
   * initialize message handler registry
   */
  constructor () {
    this.onInitializedListener = []

    // initialize ably connection
    this.onChangeListeners = []
    window.ablyConnectivity = {}
    window.ablyConnectivity.state = 'disconnected'
    window.ablyConnectivity.addOnChangeListener = (listener) => {
      this.onChangeListeners.push(listener)
    }
    window.ablyConnectivity.removeOnChangeListener = (listener) => {
      this.removeElement(this.onChangeListeners, listener)
    }

    this.enabled = false   // will be set correctly in init()
    this.ably = null
    this.appSuspended = false
    this.closeConnectionAfterResumeTimer = null

    // initialize message handler registry
    this.subscribers = {}
    this.connectHandlers = []
  }

  removeElement = (array, element) => {
    const index = array.indexOf(element)
    if (index >= 0) {
      array.splice(index, 1)
    }
  }

  async init (deviceUuid) {
    this.enabled = true
    const self = this

    const applicationInstanceId = deviceUuid

    this.ably = new BaseRealtime({
      key: ABLY_API_KEY, // Replace with a real key from the Ably dashboard
      clientId: applicationInstanceId,
      queryTime: true,
      plugins: {
        WebSocketTransport,
        FetchRequest,
        RealtimePresence,
      },
    });

    this.onInitializedListener.forEach((listener) => {
      listener()
    })

    this.ably.connection.on('initialized', () => {
      // A Connection object having this state has been initialized but no connection has yet been attempted.
      console.log('ably Connection initialized (no connection established)')
    })

    /**
     * A connection attempt has been initiated. The connecting state is entered as soon as the library has completed initialization, and is reentered each time connection is re-attempted following disconnection.
     */
    this.ably.connection.on('connecting', () => {
      console.log('ably connecting')
      if (window.ablyConnectivity.state !== 'disconnected') {
        window.ablyConnectivity.state = 'disconnected'
        self.fireChangeEvent()
      }
    })

    /**
     * A connection exists and is active.
     */
    this.ably.connection.on('connected', function () {
      console.log('ably connected')
      if (window.ablyConnectivity.state !== 'connected') {
        window.ablyConnectivity.state = 'connected'
        self.fireChangeEvent()
      }
      if (self.connectHandlers.length > 0) {
        self.connectHandlers.forEach(function (handler) {
          handler.call(self)
        })
      }
    })

    this.ably.connection.on(function (stateChange) {
      console.log('New ably connection state is ' + stateChange.current)
      window.ablyConnectivity.state = stateChange.current === 'connected' ? 'connected' : 'disconnected'
      self.fireChangeEvent()
    })

    /**
     * A temporary failure condition. No current connection exists because there is no network connectivity or no host is available.

     The disconnected state is entered if an established connection is dropped, or if a connection attempt was unsuccessful. In the disconnected state the library will periodically attempt to open a new connection (approximately every 15 seconds), anticipating that the connection will be re-established soon and thus connection and channel continuity will be possible.

     In this state, developers can continue to publish messages as they are automatically placed in a local queue, and sent as soon as a connection is established.
     Messages published by other clients to the Ably service, whilst this client is disconnected, are queued and delivered to this client upon reconnection, so long as the connection is resumed within 2 minutes.

     If connection state recovery is not possible following a new connection being established, all channels are detached, thus ensuring the developer is made aware that continuity on the channel was not possible. The developer is then responsible for re-attaching channels and recovering state, if required, using the history API.
     */
    this.ably.connection.on('disconnected', function () {
      console.log('ably disconnected but will retry')
      if (window.ablyConnectivity.state !== 'disconnected') {
        window.ablyConnectivity.state = 'disconnected'
        self.fireChangeEvent()
      }
    })

    /**
     * A long term failure condition. No current connection exists because there is no network connectivity or no host is available.

     The suspended state is entered after a failed connection attempt if there has then been no connection for a period of two minutes. In the suspended state, the library will periodically attempt to open a new connection every 30 seconds. Developers are unable to publish messages in this state. A new connection attempt can also be triggered by an explicit call to connect on the Connection object.
     */
    this.ably.connection.on('suspended', function () {
      console.log('ably suspended (long term connection failure)')

      if (window.ablyConnectivity.state !== 'disconnected') {
        window.ablyConnectivity.state = 'disconnected'
        self.fireChangeEvent()
      }

      // try again every 5 seconds
      setTimeout(self.ably.connection.connect, RECONNECTION_TIMEOUT)
    })

    /**
     * An explicit request by the developer to close the connection has been sent to the Ably service. If a reply is not received from Ably within a short period of time, the connection will be forcibly terminated and the connection state will become closed.
     */
    this.ably.connection.on('closing', function () {
      console.log('ably closing (programmatically closed)')
      if (window.ablyConnectivity.state !== 'disconnected') {
        window.ablyConnectivity.state = 'disconnected'
        self.fireChangeEvent()
      }
    })

    /**
     * The connection has been explicitly closed by the client.

     In the closed state, no reconnection attempts are made automatically by the library, and clients may not publish messages. No connection state is preserved by the service or by the library. A new connection attempt can be triggered by an explicit call to connect on the Connection object, which will result in a new connection.
     */
    this.ably.connection.on('closed', function () {
      console.log('ably closed (programmatically closed)')
      if (window.ablyConnectivity.state !== 'disconnected') {
        window.ablyConnectivity.state = 'disconnected'
        self.fireChangeEvent()
      }
    })

    /**
     * An indefinite failure condition. This state is entered if a connection error has been received from the Ably service (such as an attempt to connect with invalid credentials). A failed state may also be triggered by the client library directly as a result of some local permanent error.

     In the failed state, no reconnection attempts are made automatically by the library, and clients may not publish messages. A new connection attempt can be triggered by an explicit call to connect on the Connection object.
     */
    this.ably.connection.on('failed', function (stateChange) {
      console.error('ably connection failed: ' + stateChange.reason, JSON.stringify(stateChange))

      if (window.ablyConnectivity.state !== 'disconnected') {
        window.ablyConnectivity.state = 'disconnected'
        self.fireChangeEvent()
      }

      // try again every 5 seconds
      setTimeout(self.ably.connection.connect, RECONNECTION_TIMEOUT)
    })
  }

  async registerConnect (onConnectHandler) {
    await this.waitForInitialized()
    const position = this.connectHandlers.indexOf(onConnectHandler)
    if (position < 0) this.connectHandlers.push(onConnectHandler)
  }

  async unregisterConnect (onConnectHandler) {
    await this.waitForInitialized()
    const position = this.connectHandlers.indexOf(onConnectHandler)
    if (position >= 0) this.connectHandlers.splice(position, 1)
  }

  fireChangeEvent () {
    this.onChangeListeners.forEach(listener => listener(window.ablyConnectivity.state))
  }

  async enterPresence (channelName, data = null) {
    await this.waitForInitialized()
    const channel = await this.getChannel(channelName)
    if (channel) {
      channel.presence.enter(data, function (error) {
        if (error) {
          console.error('enter presence failed for ' + channelName)
        }
        else {
          console.log(`tell enter presence for channel ${channelName}`)
        }
      })
    }
  }

  async updatePresence (channelName, data = null) {
    await this.waitForInitialized()
    const channel = await this.getChannel(channelName)
    if (channel) {
      channel.presence.update(data, function (error) {
        if (error) {
          console.error('updated presence failed for ' + channelName)
        }
        else {
          console.log(`tell update presence for channel ${channelName}`)
        }
      })
    }
  }

  async leavePresence (channelName) {
    await this.waitForInitialized()
    const channel = await this.getChannel(channelName)
    const self = this
    if (channel) {
      channel.presence.leave()
      console.log('tell leave presence for channel ' + channelName + ' and client ' + self.ably ? self.ably.clientId : '[no-ably-instance]')
    }
  }

  async getPresence (channelName) {
    const channel = await this.getChannel(channelName)

    return new Promise((resolve, reject) => {
      channel.presence.get((error, members) => {
        if (error) {
          console.error('error getting presence list', error)
          reject(error)
        }
        console.log('There are ' + members.length + ' members on this channel')
        console.log('members are', members)
        resolve(members)
      })
    })
  }

  async subscribePresence (channelName, onChange) {
    const channel = await this.getChannel(channelName)
    if (channel) {
      channel.presence.subscribe('enter', function (member) {
        console.log(`Member ${member.clientId} / ${JSON.stringify(member.data)} entered`)
        onChange()
      })
      channel.presence.subscribe('leave', function (member) {
        console.log(`Member ${member.clientId} / ${JSON.stringify(member.data)} left`)
        onChange()
      })
      channel.presence.subscribe('update', function (member) {
        console.log(`Member ${member.clientId} / ${JSON.stringify(member.data)} updated`)
        onChange()
      })
    }
  }

  async unsubscribePresence (channelName) {
    const channel = await this.getChannel(channelName)
    channel.unsubscribe()
  }

  async getChannel (channelName, attach = true) {
    await this.waitForInitialized()

    let channel = this.ably.channels.get(channelName)
    if (attach) {
      if (channel.state !== 'attaching' && channel.state !== 'attached') {
        channel.attach(function (error) {
          if (error) {
            console.error(channelName + ' not established.', error)
          }
          else {
            console.log(`channel ${channelName} established.`)
          }
        })
      }
    }
    return channel
  }

  sendMessage (channel, eventName, message) {
    return new Promise(function (resolve, reject) {
      channel.publish(eventName, message, function (error) {
        if (error) {
          reject(error)
        }
        else {
          resolve(message)
        }
      })
    })
  }

  /**
   *
   * @param channelName
   * @param resolveFunction
   *
   * adds the resolveFunction to the list of callbacks for this channel if there is such a list
   * if not, install a default handler, add the resolveFunction to the list and touch the channel without any checks and subscribe for messages
   *
   * the resolveFunction is called with the full message {name: , data: }
   * the name is a eventName like update, data can be number, string or json
   *
   * the resolveFunction can be a storeAction
   */
  async registerChannel (channelName, resolveFunction, data) {
    // if already at least one callback is registered
    if (this.subscribers[channelName]) {
      // test if registered
      const position = this.subscribers[channelName].functions.indexOf(resolveFunction)
      // if not registered add to other callbacks
      if (position === -1) {
        this.subscribers[channelName].functions.push(resolveFunction)
      }
      // no callback registered for this channel
      const channel = await this.getChannel(channelName)
      if (channel.state !== 'attached' && channel.state !== 'attaching') {
        channel.subscribe(this.subscribers[channelName].eventHandler)
      }
      return channel
    }

    let self = this
    let channel = await this.getChannel(channelName)

    console.log('registerChannel', JSON.stringify(this.subscribers))
    // initial subscriber structure with default handler
    this.subscribers[channelName] = {
      eventHandler: function (message) {
        self.subscribers[channel.name].functions.forEach(function (callback, index) {
          try {
            callback(message, channel.name)
          }
          catch (exception) {
            console.error('Exception in handling message', message, ' on channel ', channel.name, 'function index ', index, exception)
          }
        })
      },
      functions: [resolveFunction]
    }

    channel.subscribe(this.subscribers[channelName].eventHandler)
    return channel
  }

  /**
   *
   * @param channelName
   * @param resolveFunction
   *
   * looks if there is the resolveFunction in the list of subscribers for the channelName
   * if, then the function would be removed from the list
   * if the list gets empty the defaultHandler for the channel would be also removed
   */
  async unregisterChannel (channelName, resolveFunction) {
    if (!this.ably) {
      console.error('[MessagingService] ably is not yet initialized')
      throw new Error('[MessagingService] ably is not yet initialized')
    }

    if (this.subscribers[channelName] && this.subscribers[channelName].functions && this.subscribers[channelName].functions.length > 0) {
      const position = this.subscribers[channelName].functions.indexOf(resolveFunction)
      if (position >= 0) {
        this.subscribers[channelName].functions.splice(position, 1)
      }
      if (this.subscribers[channelName].functions.length === 0) {
        let channel = await this.getChannel(channelName, false)

        console.log('unregisterChannel 2', channelName, channel.state)

        if (channel.state === 'attaching' || channel.state === 'attached') {
          const onChannelDetaching = () => {
            console.log('ably is detaching (probably after calling channel.detach())')
            channel.off('detaching', onChannelDetaching)
          }
          const onChannelDetached = () => {
            console.log('ably detached (probably after calling channel.detach())')
            channel.off('detached', onChannelDetached)
          }
          channel.on('detaching', onChannelDetaching)
          channel.on('detached', onChannelDetached)

          console.log(this.ably.channels)

          console.log(`unsubscribing and detaching from channel '${channelName}'`)
          channel.unsubscribe()
          // channel.detach((message) => {
          //   if (message) {
          //     console.log(`error detaching from channel '${channelName}`, message)
          //   }
          // })
          this.ably.channels.release(channelName)
        }
        delete this.subscribers[channelName]
      }
    }
  }

  async waitForInitialized () {
    if (this.ably) {
      return
    }
    return new Promise((resolve, reject) => {
      this.onInitializedListener.push(() => {
        resolve()
      })
    })
  }

  async clear () {
    await this.waitForInitialized()
    Object.values(this.ably.channels.all).forEach(channel => {
      channel.detach()
    })
  }

}

let messageService = new MessageService()

export default messageService
